import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, Card } from '@w7/ui'
import { bookmakerApi, bmErrorMessage, unwrapBmList } from '@/api/bookmaker'
import { getLabAdminLevel } from '@/config/lab'
import { toastCopy, useAppToast } from '@/ui/toaster'
import { BmPage } from './BmPage'
import './create-forms.css'

const PLACE_BET_TYPES = ['Match Odds', 'BookMaker', 'Racing Game'] as const

type HorseRow = { name: string; betfairStatus?: unknown }
type EventOpt = Record<string, unknown>
type UserOpt = { _id: string; userName: string }

function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}
}

export function CreateMarketPage() {
  const navigate = useNavigate()
  const toast = useAppToast()
  const { id } = useParams()
  const isEdit = Boolean(id)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [events, setEvents] = useState<EventOpt[]>([])
  const [eventQuery, setEventQuery] = useState('')
  const [assignees, setAssignees] = useState<UserOpt[]>([])
  const [isMainMarketVisible, setIsMainMarketVisible] = useState(false)
  const [markets, setMarkets] = useState<{
    name: string
    placeBetCalcType: number | ''
    delayTime: string
    bookmakerCommission: boolean
    bookmakerMultiOdds: boolean
    createdBy: string
    openDate: string
    isMainMarket: boolean
    eventDetails: EventOpt | null
    eventName: string
    event: string
    competition: string
    horses: HorseRow[]
    betfairStatus?: unknown
    _id?: string
    id?: string
  }>({
    name: '',
    placeBetCalcType: '',
    delayTime: '',
    bookmakerCommission: false,
    bookmakerMultiOdds: false,
    createdBy: '',
    openDate: '',
    isMainMarket: false,
    eventDetails: null,
    eventName: '',
    event: '',
    competition: '',
    horses: [],
  })

  useEffect(() => {
    void (async () => {
      try {
        const admin = getLabAdminLevel()
        const evData =
          admin === 'Horse Racing'
            ? await bookmakerApi.eventHorsePagination({})
            : await bookmakerApi.eventAll({})
        setEvents(unwrapBmList(evData))
      } catch (e) {
        setError(bmErrorMessage(e))
      }
      try {
        const users = await bookmakerApi.userGetAll({})
        const list = unwrapBmList(users).map((u) => ({
          _id: String(u._id ?? ''),
          userName: String(u.userName ?? u.username ?? u.name ?? ''),
        }))
        setAssignees(list.filter((u) => u._id))
      } catch {
        /* optional */
      }
    })()
  }, [])

  useEffect(() => {
    if (!id) return
    void (async () => {
      setBusy(true)
      setError(null)
      try {
        const data = asRecord(await bookmakerApi.marketGetOne({ id }))
        const horsesRaw = Array.isArray(data.horses)
          ? data.horses
          : Array.isArray(data.runners)
            ? data.runners
            : []
        setMarkets({
          name: String(data.name ?? ''),
          placeBetCalcType:
            data.placeBetCalcType === 0 || data.placeBetCalcType
              ? Number(data.placeBetCalcType)
              : '',
          delayTime: String(data.delayTime ?? data.betDelay ?? ''),
          bookmakerCommission: Boolean(data.bookmakerCommission),
          bookmakerMultiOdds: Boolean(data.bookmakerMultiOdds),
          createdBy: String(data.createdBy ?? ''),
          openDate: String(data.openDate ?? '').slice(0, 10),
          isMainMarket: Boolean(data.isMainMarket),
          eventDetails: null,
          eventName: String(data.eventName ?? ''),
          event: String(data.event ?? data.eventId ?? ''),
          competition: String(data.competition ?? data.competitionId ?? ''),
          horses: horsesRaw.map((h) => {
            const row = asRecord(h)
            return { name: String(row.name ?? ''), betfairStatus: row.betfairStatus }
          }),
          betfairStatus: data.betfairStatus,
          _id: String(data._id ?? id),
          id: String(data.betfairId ?? data.id ?? ''),
        })
      } catch (e) {
        setError(bmErrorMessage(e))
      } finally {
        setBusy(false)
      }
    })()
  }, [id])

  const filteredEvents = useMemo(() => {
    const q = eventQuery.trim().toLowerCase()
    if (!q) return events.slice(0, 80)
    return events
      .filter((e) => {
        const name = String(e.name ?? '').toLowerCase()
        const eid = String(e.id ?? e.betfairId ?? '').toLowerCase()
        return name.includes(q) || eid.includes(q)
      })
      .slice(0, 80)
  }, [events, eventQuery])

  async function onSelectEvent(ev: EventOpt) {
    setMarkets((s) => ({ ...s, eventDetails: ev }))
    setEventQuery(`${String(ev.name ?? '')} — ${String(ev.id ?? '')}`)
    try {
      const res = asRecord(
        await bookmakerApi.competitionSelectMainHorseMarket({
          event: ev._id ?? ev.id,
          competition: ev.competition ?? ev.competitionId,
        }),
      )
      setIsMainMarketVisible(Boolean(res.isNotMainMarket))
    } catch {
      setIsMainMarketVisible(false)
    }
  }

  function failSave(msg: string) {
    setError(msg)
    toast.show(toastCopy.marketSaveError(isEdit, msg))
  }

  async function onSubmit() {
    if (!markets.name.trim()) {
      failSave('Insert Valid Name')
      return
    }
    if (!isEdit && !markets.eventDetails) {
      failSave('Select Event')
      return
    }
    if (markets.placeBetCalcType === '') {
      failSave('Select Calculation Type')
      return
    }
    if (!String(markets.delayTime).trim()) {
      failSave('Select Delay Time')
      return
    }
    if (!markets.createdBy) {
      failSave('Select Assignee')
      return
    }

    setBusy(true)
    setError(null)
    try {
      if (isEdit) {
        const betfairId = String(markets.id ?? markets._id ?? id ?? '')
        const body: Record<string, unknown> = {
          ...markets,
          _id: markets._id ?? id,
          id: betfairId,
          betfairId,
          marketId: betfairId,
          placeBetCalcType: Number(markets.placeBetCalcType),
          delayTime: Number(markets.delayTime),
          horses: markets.horses.filter((h) => h.name.trim()),
          runners: markets.horses.filter((h) => h.name.trim()),
        }
        delete (body as { eventDetails?: unknown }).eventDetails
        await bookmakerApi.marketEdit(body)
        toast.show(toastCopy.marketUpdated)
      } else {
        const ev = markets.eventDetails!
        const horses = markets.horses
          .filter((h) => h.name.trim())
          .map((h) => ({
            ...h,
            betfairStatus: markets.betfairStatus,
          }))
        const body: Record<string, unknown> = {
          name: markets.name.trim(),
          placeBetCalcType: Number(markets.placeBetCalcType),
          delayTime: Number(markets.delayTime),
          bookmakerCommission: markets.bookmakerCommission,
          bookmakerMultiOdds: markets.bookmakerMultiOdds,
          createdBy: markets.createdBy,
          isMainMarket: markets.isMainMarket,
          horses,
          event: ev._id ?? ev.id,
          competition: ev.competition ?? ev.competitionId,
          eventBetfairId: ev.id ?? ev.betfairId,
          eventId: ev.id ?? ev.betfairId,
          custom: ev.custom,
          marketBetfairId: ev.marketBetfairId,
          betfairStatus: markets.betfairStatus,
        }
        if (markets.name === 'Match Odds' && markets.openDate) {
          body.openDate = markets.openDate
        }
        await bookmakerApi.marketCreate(body)
        toast.show(toastCopy.marketCreated)
      }
      navigate('/marketlist')
    } catch (e) {
      failSave(bmErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <BmPage
      title={isEdit ? 'Edit Market' : 'Create Market'}
      description="Lab create/edit market — Bookmaker-API assigns id as {betfairId}#bookmaker{n}"
      error={error}
      actions={
        <Button variant="secondary" onClick={() => navigate('/marketlist')}>
          Cancel
        </Button>
      }
    >
      <Card>
        <div className="bm-create">
          <div className="bm-create-row">
            <label htmlFor="bm-mkt-name">Market Name</label>
            <input
              id="bm-mkt-name"
              type="text"
              value={markets.name}
              onChange={(e) => setMarkets((s) => ({ ...s, name: e.target.value }))}
              placeholder="Insert Market Name"
            />
          </div>

          <div className="bm-create-row">
            <label htmlFor="bm-mkt-event">Event Name</label>
            {isEdit ? (
              <input id="bm-mkt-event" type="text" value={markets.eventName || markets.event} disabled />
            ) : (
              <div>
                <input
                  id="bm-mkt-event"
                  type="text"
                  list="bm-event-options"
                  value={eventQuery}
                  onChange={(e) => {
                    setEventQuery(e.target.value)
                    const hit = events.find(
                      (ev) =>
                        `${String(ev.name ?? '')} — ${String(ev.id ?? '')}` === e.target.value ||
                        String(ev.id) === e.target.value ||
                        String(ev._id) === e.target.value,
                    )
                    if (hit) void onSelectEvent(hit)
                  }}
                  placeholder="Enter event name or Id"
                />
                <datalist id="bm-event-options">
                  {filteredEvents.map((ev) => (
                    <option
                      key={String(ev._id ?? ev.id)}
                      value={`${String(ev.name ?? '')} — ${String(ev.id ?? '')}`}
                    />
                  ))}
                </datalist>
                <p className="bm-create-hint">
                  Pick an existing Betfair/custom event — market id becomes base#bookmakerN
                </p>
              </div>
            )}
          </div>

          <div className="bm-create-row">
            <label htmlFor="bm-mkt-calc">Place Bet Calculation Type</label>
            <select
              id="bm-mkt-calc"
              value={markets.placeBetCalcType === '' ? '' : String(markets.placeBetCalcType)}
              onChange={(e) =>
                setMarkets((s) => ({
                  ...s,
                  placeBetCalcType: e.target.value === '' ? '' : Number(e.target.value),
                }))
              }
            >
              <option value="">Select…</option>
              {PLACE_BET_TYPES.map((label, index) => (
                <option key={label} value={index}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div className="bm-create-row">
            <label htmlFor="bm-mkt-delay">Bet Delay Time</label>
            <input
              id="bm-mkt-delay"
              type="number"
              min={0}
              value={markets.delayTime}
              onChange={(e) => setMarkets((s) => ({ ...s, delayTime: e.target.value }))}
              placeholder="seconds"
            />
          </div>

          <div className="bm-create-row">
            <label>Commission</label>
            <label className="bm-create-check">
              <input
                type="checkbox"
                checked={markets.bookmakerCommission}
                onChange={(e) => setMarkets((s) => ({ ...s, bookmakerCommission: e.target.checked }))}
              />
              {markets.bookmakerCommission ? 'Enabled' : 'Disabled'}
            </label>
          </div>

          <div className="bm-create-row">
            <label>Multiple Odds (6 Odds)</label>
            <label className="bm-create-check">
              <input
                type="checkbox"
                checked={markets.bookmakerMultiOdds}
                onChange={(e) => setMarkets((s) => ({ ...s, bookmakerMultiOdds: e.target.checked }))}
              />
              {markets.bookmakerMultiOdds ? 'True' : 'False'}
            </label>
          </div>

          <div className="bm-create-row">
            <label htmlFor="bm-mkt-assignee">Add Assignee</label>
            <select
              id="bm-mkt-assignee"
              value={markets.createdBy}
              onChange={(e) => setMarkets((s) => ({ ...s, createdBy: e.target.value }))}
            >
              <option value="">Select…</option>
              {assignees.map((u) => (
                <option key={u._id} value={u._id}>
                  {u.userName}
                </option>
              ))}
            </select>
          </div>

          {markets.name === 'Match Odds' ? (
            <div className="bm-create-row">
              <label htmlFor="bm-mkt-open">Open Date</label>
              <input
                id="bm-mkt-open"
                type="date"
                value={markets.openDate}
                onChange={(e) => setMarkets((s) => ({ ...s, openDate: e.target.value }))}
              />
            </div>
          ) : null}

          {isMainMarketVisible ? (
            <div className="bm-create-row">
              <label>Select as main market</label>
              <label className="bm-create-check">
                <input
                  type="checkbox"
                  checked={markets.isMainMarket}
                  onChange={(e) => setMarkets((s) => ({ ...s, isMainMarket: e.target.checked }))}
                />
              </label>
            </div>
          ) : null}

          <div className="bm-create-horses">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong>Create Horses</strong>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setMarkets((s) => ({ ...s, horses: [...s.horses, { name: '' }] }))}
              >
                Add
              </Button>
            </div>
            <div className="bm-create-horse-head">
              <span>Sr</span>
              <span>Horse Name</span>
              <span>Action</span>
            </div>
            {markets.horses.map((horse, index) => (
              <div className="bm-create-horse-row" key={index}>
                <span>{index + 1}</span>
                <input
                  type="text"
                  value={horse.name}
                  placeholder="Insert Horse Name"
                  onChange={(e) =>
                    setMarkets((s) => {
                      const horses = [...s.horses]
                      horses[index] = { ...horses[index]!, name: e.target.value }
                      return { ...s, horses }
                    })
                  }
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() =>
                    setMarkets((s) => ({
                      ...s,
                      horses: s.horses.filter((_, i) => i !== index),
                    }))
                  }
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>

          <div className="bm-create-actions">
            <Button type="button" disabled={busy} onClick={() => void onSubmit()}>
              {busy ? 'Working…' : 'Submit'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => navigate('/marketlist')}>
              Cancel
            </Button>
          </div>
        </div>
      </Card>
    </BmPage>
  )
}
